import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Plus, Trash2, Download } from "lucide-react";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { Button } from "@/components/ui/button";
import { trpcClient } from "@/lib/trpc";
import { ConfigLabel } from "@/components/shared/ConfigLabel";
import type { FilterField } from "@/components/shared/FilterBar";
import { useConfirm } from "@/providers/ConfirmProvider";
import { toast } from "sonner";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { InlineFilterRow } from "@/components/shared/InlineFilterRow";
import { Skeleton } from "@/components/ui/skeleton";
import { formatNumber, cn, extractErrorMessage } from "@/lib/utils";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { downloadExcelTemplate, exportToExcel, type ExcelColumnDef } from "@/lib/excel.utils";
import { ImportDialog } from "@/components/shared/ImportDialog";
import { useImportRefs } from "@/hooks/useImportRefs";
import { normKey, ROW_KEY, stripMeta, type ImportResult, type ImportRowResult } from "@/lib/import-kit";
import { useConfigLookup } from "@/hooks/useConfigLookup";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";

/** Cột Excel CCDC (05/11/2026): Phân loại / ĐVT xuất tên hiển thị như trên màn (trước xuất mã cấu hình) */
const buildExportColumns = (catLabel: (v: string) => string, unitLabel: (v: string) => string): ExcelColumnDef[] => [
  { key: "code", label: "Mã CCDC", width: 18, required: true },
  { key: "name", label: "Tên CCDC", width: 34, required: true },
  { key: "category", label: "Phân Loại", width: 18, ref: "toolingCategory", format: (v) => catLabel(v) },
  { key: "unit", label: "Đơn Vị Tính", width: 12, ref: "toolingUnit", format: (v) => unitLabel(v) },
  { key: "status", label: "Trạng Thái", width: 14, ref: "toolingStatus", format: (v) => (v === "INACTIVE" ? "Ngưng dùng" : v === "ACTIVE" ? "Đang dùng" : v || "") },
];

const FILTER_FIELDS: FilterField[] = [
  { key: "q", label: "Từ khóa", type: "text", placeholder: "Tìm mã, tên..." },
  {
    key: "status",
    label: "Trạng thái",
    type: "combobox-multi",
    options: [
      { value: "ACTIVE", label: "Hoạt động" },
      { value: "INACTIVE", label: "Ngưng" },
    ],
  },
];

export default function ToolingItemListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const {
    paginatedItems: data,
    isLoading,
    filterValues,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "tooling-items",
    queryFn: async (params) => {
      const res = await trpcClient.tooling.listItemsPaginated.query(params);
      return {
        items: res.data,
        meta: {
          total: res.total,
          totalPages: res.totalPages,
          page: res.page,
          limit: res.pageSize,
        },
      };
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (variables: { id: string }) =>
      trpcClient.tooling.deleteItem.mutate(variables),
    onSuccess: () => {
      toast.success("Xóa thành công");
      queryClient.invalidateQueries({ queryKey: ["tooling-items"] });
    },
    onError: (err: any) => {
      toast.error(`Không thể xóa: ${err.message}`);
    },
  });

  const handleDelete = async (id: string, name: string) => {
    const isConfirmed = await confirm({
      title: "Xác nhận xóa",
      message: `Bạn có chắc chắn muốn xóa CCDC "${name}"? Hành động này không thể hoàn tác.`,
      confirmText: "Xóa",
      cancelText: "Hủy",
    });

    if (isConfirmed) {
      deleteMutation.mutate({ id });
    }
  };

  const COLS = 7;

  // ── Chọn nhiều dòng + thao tác hàng loạt ──
  const { hasPermission } = useRoleAccess();
  // Nhập Excel CCDC (05/11/2026): mã đã có → cập nhật (giữ trường file để trống), mới → tạo; Phân loại / ĐVT / Trạng thái
  // khớp theo tên hoặc mã, chưa có thì hỏi thêm; báo cáo từng dòng
  const [showImport, setShowImport] = useState(false);
  const importRefs = useImportRefs();
  const toolingImportRefs = useMemo(
    () => ({ toolingCategory: importRefs.toolingCategory, toolingUnit: importRefs.toolingUnit, toolingStatus: importRefs.toolingStatus }),
    [importRefs],
  );
  const handleImport = async (rows: Record<string, unknown>[]): Promise<ImportResult> => {
    const res: any = await trpcClient.tooling.listItems.query();
    const all: any[] = Array.isArray(res) ? res : res?.items || res?.data || [];
    const byCode = new Map(all.map((t) => [normKey(t.code), t]));
    const results: ImportRowResult[] = [];
    for (const row of rows) {
      const rowNo = Number(row[ROW_KEY]) || 0;
      const data = stripMeta(row) as any;
      const code = String(data.code ?? "").trim();
      try {
        const cur = byCode.get(normKey(code));
        if (cur) {
          await trpcClient.tooling.updateItem.mutate({
            id: cur.id,
            data: {
              code: cur.code,
              name: data.name ?? cur.name,
              category: data.category ?? cur.category ?? null,
              unit: data.unit ?? cur.unit ?? null,
              description: cur.description ?? null,
              status: data.status ?? cur.status ?? null,
            },
          });
          results.push({ row: rowNo, code, status: "updated" });
        } else {
          await trpcClient.tooling.createItem.mutate(data);
          results.push({ row: rowNo, code, status: "created" });
        }
      } catch (err) {
        results.push({ row: rowNo, code, status: "failed", message: extractErrorMessage(err) || "Lỗi" });
      }
    }
    await queryClient.invalidateQueries({ queryKey: ["tooling-items"] });
    return { rows: results };
  };
  const rowIds = useMemo(() => (data || []).map((i: any) => i.id), [data]);
  const categoryLookup = useConfigLookup("TOOLING_CATEGORY");
  const unitLookup = useConfigLookup("UNIT_TOOLING");
  const EXPORT_COLUMNS = useMemo(() => {
    const labelOf = (opts: any[]) => (v: string) => opts.find((o) => o.id === v || o.value === v)?.label || v || "";
    return buildExportColumns(labelOf(categoryLookup.options), labelOf(unitLookup.options));
  }, [categoryLookup.options, unitLookup.options]);
  // Xuất Excel theo quyền Xuất DL của bảng phân quyền (trước nút thường, ai cũng xuất được)
  const { canExport } = useModuleAccess();
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;
  const bulkActions: BulkAction[] = selectedCount
    ? [
        ...(canExport ? [{
          icon: <Download className="h-4 w-4" />,
          label: "Xuất Excel",
          variant: "outline",
          onClick: () =>
            exportToExcel(
              (data || []).filter((i: any) => selectedIds.has(i.id)),
              EXPORT_COLUMNS,
              "ccdc_da_chon",
            ),
        } as BulkAction] : []),
        ...(hasPermission("molds", "delete")
          ? [
              {
                icon: <Trash2 className="h-4 w-4" />,
                label: `Xoá (${selectedCount})`,
                variant: "destructive" as const,
                onClick: () =>
                  executeBulkAction({
                    confirmFn: confirm,
                    selectedIds: Array.from(selectedIds) as string[],
                    actionFn: async (id: string) => trpcClient.tooling.deleteItem.mutate({ id }),
                    actionName: "xóa",
                    itemType: "CCDC",
                    onSuccess: () => {
                      clearSelection();
                      queryClient.invalidateQueries({ queryKey: ["tooling-items"] });
                    },
                    requireConfirm: true,
                    confirmMessage: `Xóa ${selectedCount} CCDC đã chọn? Hành động không thể hoàn tác.`,
                  }),
              },
            ]
          : []),
      ]
    : [];

  return (
    <ListPageShell
      unitLabel="CCDC"
      filterValues={filterValues}
      filterFields={FILTER_FIELDS}
      showFilter={showFilter}
      onToggleFilter={onToggleFilter}
      onClearAll={onClearAll}
      actions={
        <>
          <ExportImportButtons
            onExport={() => exportToExcel(data || [], EXPORT_COLUMNS, "danh_muc_ccdc")}
            onImportClick={hasPermission("molds", "create") ? () => setShowImport(true) : undefined}
          />
          <Button onClick={() => navigate({ to: "/master/tooling/new" })}>
            <Plus className="h-4 w-4 mr-2" />
            Thêm mới
          </Button>
        </>
      }
      isLoading={isLoading}
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems || 0}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    >
      <table className={cn("w-full text-xs caption-bottom apv-compact-table", rowSelection.selectionMode && "apv-select-mode")}>
        <thead className="apv-table-header">
          <tr>
            <DataTableSelectAllCheckbox rowSelection={rowSelection} />
            <SortableHeader
              label="Mã CCDC"
              field="code"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="apv-col-code w-[120px]"
            />
            <SortableHeader
              label="Tên CCDC"
              field="name"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
            />
            <SortableHeader
              label="Phân loại"
              field="category"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-[150px]"
            />
            <SortableHeader
              label="Đơn vị tính"
              field="unit"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-[100px]"
            />
            <SortableHeader
              label="Trạng thái"
              field="status"
              currentField={sortField}
              currentOrder={sortOrder}
              onSort={handleSort}
              className="w-[120px]"
            />
            <th className="text-right w-[80px] px-2 py-1.5"></th>
          </tr>
          {showFilter && (
            <InlineFilterRow
              columns={[
                null,
                { key: "q" },
                { key: "q" },
                null,
                null,
                { key: "status" },
                null,
              ]}
              filterFields={FILTER_FIELDS}
              filterValues={filterValues}
              onFilterChange={onFilterChange}
            />
          )}
        </thead>
        <tbody>
          {isLoading ? (
            Array.from({ length: 10 }).map((_, i) => (
              <tr key={i}>
                {Array.from({ length: COLS }).map((_, j) => (
                  <td key={j} className="px-2 py-2">
                    <Skeleton className="h-3 w-12" />
                  </td>
                ))}
              </tr>
            ))
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={COLS}
                className="text-center text-slate-400 py-12 text-sm"
              >
                Không tìm thấy dữ liệu CCDC
              </td>
            </tr>
          ) : (
            data.map((item: any) => (
              <DataTableRow
                key={item.id}
                id={item.id}
                rowSelection={rowSelection}
                className="hover:bg-blue-50/40 transition-colors border-b border-slate-100 last:border-0 cursor-pointer"
                onClick={() => navigate({ to: `/master/tooling/${item.id}` as any })}
              >
                <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} />
                <td className="apv-col-code px-2 py-1.5">
                  <span className="font-semibold text-indigo-600 font-mono">
                    {item.code}
                  </span>
                </td>
                <td className="px-2 py-1.5 font-medium text-slate-900">
                  {item.name}
                </td>
                <td className="px-2 py-1.5">
                  {item.category ? (
                    <ConfigLabel
                      categoryCode="TOOLING_CATEGORY"
                      value={item.category}
                    />
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-2 py-1.5">
                  {item.unit ? (
                    <ConfigLabel
                      categoryCode="UNIT_TOOLING"
                      value={item.unit}
                    />
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-2 py-1.5">
                  <span
                    className={`px-2 py-1 rounded-full text-[10px] font-medium ${
                      item.status === "ACTIVE"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {item.status || "—"}
                  </span>
                </td>
                <td
                  className="px-2 py-1.5 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive"
                      onClick={() => handleDelete(item.id, item.name)}
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </td>
              </DataTableRow>
            ))
          )}
        </tbody>
      </table>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />
      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        title="Import Danh Mục CCDC"
        columns={EXPORT_COLUMNS}
        refs={toolingImportRefs}
        onImport={handleImport}
        onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "danh_muc_ccdc")}
      />
    </ListPageShell>
  );
}
